import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

// Pixel font URLs (will be loaded via link tag in head)

// =============================================================================
// DESIGN TOKENS - Pixel Art Aesthetic
// =============================================================================

const theme = {
  colors: {
    bg: '#0d0814',
    card: '#1a1225',
    cardHover: '#251a35',
    border: '#3d2a54',
    borderLight: '#5a4a6e',
    primary: '#64ffda',
    primaryDim: 'rgba(100, 255, 218, 0.2)',
    secondary: '#ff9f43',
    secondaryDim: 'rgba(255, 159, 67, 0.2)',
    accent: '#a855f7',
    success: '#00d9a4',
    warning: '#ffc107',
    danger: '#ff6b6b',
    text: '#f5f0e1',
    textMuted: '#b8a8c8',
    textDim: '#7a6a8a',
  },
  fonts: {
    header: "'Press Start 2P', cursive",
    body: "'VT323', monospace",
  },
  sizes: {
    pixel: 4, // Base pixel unit
  }
};

// =============================================================================
// PIXEL ART ICONS (SVG)
// =============================================================================

const PixelIcons = {
  // Dashboard icon
  Dashboard: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 3h8v8H3V3zm0 10h8v8H3v-8zm10-10h8v8h-8V3zm0 10h8v8h-8v-8z"/>
    </svg>
  ),
  // Chart icon
  Chart: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 20h3v-9H3v9zm5 0h3V4H8v16zm5 0h3v-5h-3v5zm5 0h3v-7h-3v7z"/>
    </svg>
  ),
  // User/person icon
  User: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
  ),
  // Flame/hot icon
  Flame: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 23c4.97 0 9-4.03 9-9 0-4.97-9-13-9-13S3 9.03 3 14c0 4.97 4.03 9 9 9zm0-16c1.5 2.5 4 5.5 4 9 0 2.21-1.79 4-4 4s-4-1.79-4-4c0-3.5 2.5-6.5 4-9z"/>
    </svg>
  ),
  // Lightning bolt
  Zap: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7 2v11h3v9l7-12h-4l4-8z"/>
    </svg>
  ),
  // Clock/time icon
  Clock: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
    </svg>
  ),
  // Git branch
  GitBranch: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 3c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm0 14c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm10-9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm-5 3c0-2.42-1.72-4.44-4-4.9V6.1c2.28-.46 4-2.48 4-4.9h2c0 2.42 1.72 4.44 4 4.9v4.1c-2.28.46-4 2.48-4 4.9h-2z"/>
    </svg>
  ),
  // Trophy
  Trophy: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l-5.5 9h11L12 2zm0 3.84L13.93 9h-3.86L12 5.84zM17.5 13c-2.48 0-4.5 2.02-4.5 4.5s2.02 4.5 4.5 4.5 4.5-2.02 4.5-4.5-2.02-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM6.5 13C4.02 13 2 15.02 2 17.5S4.02 22 6.5 22s4.5-2.02 4.5-4.5S8.98 13 6.5 13zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  ),
  // Arrow right
  ArrowRight: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
    </svg>
  ),
  // Settings/gear
  Settings: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L5.09 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.04.64.09.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.04.24.24.41.48.41h3.84c.24 0 .43-.17.47-.41l.36-2.54c.59-.24 1.13-.57 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.08-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
    </svg>
  ),
  // Eye (for view toggling)
  Eye: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
    </svg>
  ),
  // Shield (admin)
  Shield: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
    </svg>
  ),
};

// =============================================================================
// MOCK DATA - Simulated OTel-derived metrics
// =============================================================================

const mockData = {
  currentUser: {
    id: 'user-1',
    name: 'You',
    avatar: '👾',
    role: 'senior',
    isAdmin: false,
  },

  // Personal metrics (current user)
  personal: {
    // Code Quality Score (0-100)
    qualityScore: {
      current: 87,
      previous: 82,
      trend: '+6%',
      breakdown: {
        toolAcceptance: 92,
        errorRate: 8,
        codeReviewPass: 85,
        testCoverage: 78
      }
    },

    // PR Efficiency
    prEfficiency: {
      created: 23,
      merged: 21,
      mergeRate: 91,
      avgMergeTime: 4.2, // hours
      aiContribution: 67, // % of PR code AI-assisted
      timeToFirstReview: 1.8,
      sizeDistribution: [
        { size: 'XS (<50)', count: 5, avgMergeTime: 2.1 },
        { size: 'S (50-200)', count: 8, avgMergeTime: 3.2 },
        { size: 'M (200-500)', count: 6, avgMergeTime: 4.8 },
        { size: 'L (500-1000)', count: 3, avgMergeTime: 7.2 },
        { size: 'XL (>1000)', count: 1, avgMergeTime: 12.5 },
      ],
    },

    // Token Efficiency
    tokenEfficiency: {
      totalTokens: 2847500,
      linesChanged: 12847,
      ratio: 222, // tokens per line changed (lower is better)
      ratioPercentile: 78, // vs team
      trend: 'improving',
      byModel: {
        'claude-opus-4': { tokens: 845000, lines: 3200, ratio: 264 },
        'claude-sonnet-4': { tokens: 1685000, lines: 7847, ratio: 215 },
        'claude-haiku-4': { tokens: 317500, lines: 1800, ratio: 176 },
      }
    },

    // Session Engagement
    sessions: {
      total: 127,
      avgDuration: 42, // minutes
      mostActiveDay: 'Tuesday',
      mostActiveHour: 14, // 2pm
      streak: 5, // days
      weeklyData: [
        { day: 'Mon', sessions: 3, minutes: 95, tokens: 145000 },
        { day: 'Tue', sessions: 5, minutes: 180, tokens: 287000 },
        { day: 'Wed', sessions: 4, minutes: 142, tokens: 198000 },
        { day: 'Thu', sessions: 6, minutes: 210, tokens: 312000 },
        { day: 'Fri', sessions: 4, minutes: 156, tokens: 234000 },
        { day: 'Sat', sessions: 2, minutes: 68, tokens: 89000 },
        { day: 'Sun', sessions: 1, minutes: 32, tokens: 45000 },
      ]
    },

    // Tool Acceptance Rate
    toolAcceptance: {
      overall: 78,
      byTool: {
        Edit: 85,
        Write: 72,
        Read: 95,
        Bash: 68,
        MultiEdit: 81,
        NotebookEdit: 75,
      },
      trend: [
        { week: 'W1', rate: 72 },
        { week: 'W2', rate: 75 },
        { week: 'W3', rate: 74 },
        { week: 'W4', rate: 78 },
      ]
    },

    // Productivity Heatmap (hour x day of week)
    productivityHeatmap: {
      // Rows: hours 8-20, Cols: Mon-Sun
      // Values: 0-100 (productivity score)
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      hours: ['8a', '10a', '12p', '2p', '4p', '6p', '8p'],
      data: [
        [20, 45, 65, 80, 75, 40, 15],  // 8a
        [35, 72, 88, 95, 85, 50, 20],  // 10a
        [45, 80, 85, 90, 82, 55, 25],  // 12p
        [55, 88, 92, 100, 88, 60, 30], // 2p
        [50, 85, 80, 85, 75, 52, 28],  // 4p
        [40, 70, 65, 70, 60, 45, 22],  // 6p
        [25, 45, 40, 45, 40, 30, 18],  // 8p
      ]
    },

    // Reversion Rate - how much of your code gets changed/reverted downstream
    reversionRate: {
      overall: 8.4, // % of lines reverted or heavily modified
      teamAverage: 12.3,
      trend: 'improving', // 'improving' | 'stable' | 'worsening'
      breakdown: {
        fullyReverted: 3.2, // % completely reverted
        heavilyModified: 5.2, // % changed >50% in follow-ups
        lightlyTouched: 15.8, // % changed <50%
        untouched: 75.8, // % unchanged
      },
      byTimeSinceMerge: [
        { week: 'Week 1', reverted: 2.1, modified: 4.5 },
        { week: 'Week 2', reverted: 1.8, modified: 3.2 },
        { week: 'Week 3', reverted: 0.9, modified: 2.1 },
        { week: 'Week 4+', reverted: 0.4, modified: 1.8 },
      ],
      topFiles: [
        { file: 'AuthService.tsx', reverted: 2, modified: 5, churn: 28 },
        { file: 'PaymentFlow.tsx', reverted: 0, modified: 3, churn: 15 },
        { file: 'Dashboard.jsx', reverted: 1, modified: 2, churn: 12 },
        { file: 'APIRoutes.js', reverted: 0, modified: 1, churn: 8 },
      ],
      weeklyTrend: [
        { week: 'W1', rate: 11.2 },
        { week: 'W2', rate: 10.5 },
        { week: 'W3', rate: 9.1 },
        { week: 'W4', rate: 8.4 },
      ],
    },
  },

  // Team aggregates (anonymized for non-admins)
  team: {
    totalMembers: 12,
    activeNow: 7,

    // Averages
    averages: {
      qualityScore: 76,
      prEfficiency: { mergeRate: 84, avgMergeTime: 6.8 },
      tokenEfficiency: { ratio: 285 },
      toolAcceptance: 71,
    },

    // Percentiles (for comparison)
    percentiles: {
      p25: { quality: 62, ratio: 340 },
      p50: { quality: 76, ratio: 285 },
      p75: { quality: 88, ratio: 215 },
      p90: { quality: 94, ratio: 178 },
    },

    // Leaderboard (anonymized - shows rank, not names)
    leaderboard: [
      { rank: 1, avatar: '🤖', score: 94, prs: 31, isYou: false },
      { rank: 2, avatar: '🦊', score: 91, prs: 28, isYou: false },
      { rank: 3, avatar: '👾', score: 87, prs: 23, isYou: true },  // You!
      { rank: 4, avatar: '🚀', score: 85, prs: 26, isYou: false },
      { rank: 5, avatar: '⚡', score: 82, prs: 19, isYou: false },
    ],

    // Model usage distribution
    modelDistribution: [
      { model: 'Sonnet 4', usage: 58, avgQuality: 79 },
      { model: 'Opus 4', usage: 28, avgQuality: 88 },
      { model: 'Haiku 4', usage: 14, avgQuality: 65 },
    ],
  },

  // Admin-only data (people + agent focused)
  adminData: {
    // Full team leaderboard (de-anonymized for admin)
    fullTeam: [
      { id: 'user-1', name: 'You', avatar: '👾', score: 87, prs: 23, tokens: 2847500, acceptance: 78, sessions: 127, isYou: true },
      { id: 'user-2', name: 'Alex', avatar: '🤖', score: 94, prs: 31, tokens: 3245000, acceptance: 84, sessions: 156, isYou: false },
      { id: 'user-3', name: 'Jordan', avatar: '🦊', score: 91, prs: 28, tokens: 2890000, acceptance: 81, sessions: 142, isYou: false },
      { id: 'user-4', name: 'Sam', avatar: '🚀', score: 85, prs: 26, tokens: 2670000, acceptance: 76, sessions: 118, isYou: false },
      { id: 'user-5', name: 'Taylor', avatar: '⚡', score: 82, prs: 19, tokens: 2340000, acceptance: 73, sessions: 98, isYou: false },
      { id: 'user-6', name: 'Casey', avatar: '🎯', score: 79, prs: 22, tokens: 2510000, acceptance: 71, sessions: 134, isYou: false },
      { id: 'user-7', name: 'Riley', avatar: '💫', score: 76, prs: 17, tokens: 2120000, acceptance: 69, sessions: 89, isYou: false },
      { id: 'user-8', name: 'Quinn', avatar: '🌟', score: 73, prs: 15, tokens: 1980000, acceptance: 67, sessions: 76, isYou: false },
      { id: 'user-9', name: 'Avery', avatar: '🔮', score: 68, prs: 12, tokens: 1750000, acceptance: 64, sessions: 62, isYou: false },
      { id: 'user-10', name: 'Morgan', avatar: '🎨', score: 65, prs: 14, tokens: 1890000, acceptance: 62, sessions: 81, isYou: false },
      { id: 'user-11', name: 'Blake', avatar: '🎪', score: 58, prs: 8, tokens: 1450000, acceptance: 58, sessions: 45, isYou: false },
      { id: 'user-12', name: 'Drew', avatar: '🎭', score: 52, prs: 6, tokens: 1230000, acceptance: 55, sessions: 38, isYou: false },
    ],

    // Full team roster with status (for Team Roster card)
    teamRoster: [
      { id: 'user-1', name: 'You', avatar: '👾', role: 'Senior Engineer', status: 'online', currentSession: { model: 'Sonnet 4', duration: 18, tokens: 45678 }, todaySessions: 4, todayTokens: 145000, weekTokens: 892000 },
      { id: 'user-2', name: 'Alex', avatar: '🤖', role: 'Staff Engineer', status: 'online', currentSession: { model: 'Opus 4', duration: 42, tokens: 123456 }, todaySessions: 6, todayTokens: 234000, weekTokens: 1245000 },
      { id: 'user-3', name: 'Jordan', avatar: '🦊', role: 'Senior Engineer', status: 'away', currentSession: null, todaySessions: 3, todayTokens: 89000, weekTokens: 678000 },
      { id: 'user-4', name: 'Sam', avatar: '🚀', role: 'Engineer', status: 'online', currentSession: { model: 'Sonnet 4', duration: 25, tokens: 67890 }, todaySessions: 5, todayTokens: 156000, weekTokens: 890000 },
      { id: 'user-5', name: 'Taylor', avatar: '⚡', role: 'Engineer', status: 'offline', currentSession: null, todaySessions: 0, todayTokens: 0, weekTokens: 234000 },
      { id: 'user-6', name: 'Casey', avatar: '🎯', role: 'Senior Engineer', status: 'online', currentSession: { model: 'Haiku 4', duration: 8, tokens: 12345 }, todaySessions: 7, todayTokens: 198000, weekTokens: 756000 },
      { id: 'user-7', name: 'Riley', avatar: '💫', role: 'Junior Engineer', status: 'online', currentSession: { model: 'Sonnet 4', duration: 31, tokens: 89012 }, todaySessions: 4, todayTokens: 134000, weekTokens: 567000 },
      { id: 'user-8', name: 'Quinn', avatar: '🌟', role: 'Engineer', status: 'away', currentSession: null, todaySessions: 2, todayTokens: 45000, weekTokens: 345000 },
      { id: 'user-9', name: 'Avery', avatar: '🔮', role: 'Junior Engineer', status: 'offline', currentSession: null, todaySessions: 0, todayTokens: 0, weekTokens: 189000 },
      { id: 'user-10', name: 'Morgan', avatar: '🎨', role: 'Designer-Dev', status: 'online', currentSession: { model: 'Sonnet 4', duration: 52, tokens: 145678 }, todaySessions: 5, todayTokens: 178000, weekTokens: 623000 },
      { id: 'user-11', name: 'Blake', avatar: '🎪', role: 'Intern', status: 'online', currentSession: { model: 'Haiku 4', duration: 15, tokens: 23456 }, todaySessions: 8, todayTokens: 89000, weekTokens: 234000 },
      { id: 'user-12', name: 'Drew', avatar: '🎭', role: 'Junior Engineer', status: 'offline', currentSession: null, todaySessions: 1, todayTokens: 12000, weekTokens: 156000 },
    ],

    // Cost tracking by person and model
    costs: {
      today: 127.50,
      week: 892.30,
      month: 3456.80,
      trend: '+12%',
      byPerson: [
        { name: 'Alex', avatar: '🤖', today: 23.40, week: 156.20, month: 456.20, pct: 28 },
        { name: 'Morgan', avatar: '🎨', today: 18.90, week: 134.50, month: 389.50, pct: 22 },
        { name: 'You', avatar: '👾', today: 15.20, week: 112.30, month: 312.40, pct: 18 },
        { name: 'Sam', avatar: '🚀', today: 12.80, week: 98.60, month: 298.80, pct: 15 },
        { name: 'Riley', avatar: '💫', today: 11.50, week: 89.40, month: 267.30, pct: 14 },
        { name: 'Others', avatar: '📊', today: 45.70, week: 301.30, month: 1732.60, pct: 3 },
      ],
      byModel: [
        { model: 'Sonnet 4', cost: 1856.40, pct: 54, trend: 'up' },
        { model: 'Opus 4', cost: 1234.80, pct: 36, trend: 'stable' },
        { model: 'Haiku 4', cost: 365.60, pct: 10, trend: 'up' },
      ],
      forecast: { nextWeek: 945.00, nextMonth: 3780.00 },
    },

    // Team health metrics
    teamHealth: {
      burnoutRisk: [
        { name: 'Casey', avatar: '🎯', risk: 'high', reason: '7 sessions today, low acceptance' },
        { name: 'Blake', avatar: '🎪', risk: 'medium', reason: '8 sessions, high token usage' },
        { name: 'Alex', avatar: '🤖', risk: 'low', reason: 'balanced usage' },
      ],
      streakData: [
        { name: 'Alex', avatar: '🤖', streak: 23, status: 'active' },
        { name: 'You', avatar: '👾', streak: 5, status: 'active' },
        { name: 'Sam', avatar: '🚀', streak: 12, status: 'active' },
        { name: 'Morgan', avatar: '🎨', streak: 8, status: 'paused' },
      ],
      activityPatterns: [
        { name: 'Alex', avatar: '🤖', peakHours: ['10a-12p', '2p-4p'], avgSessionTime: 38 },
        { name: 'You', avatar: '👾', peakHours: ['10a-12p', '2p-4p'], avgSessionTime: 42 },
        { name: 'Casey', avatar: '🎯', peakHours: ['8a-10a', '6p-8p'], avgSessionTime: 18 },
      ],
    },

    // Agent adoption and usage patterns
    agentAdoption: {
      byPerson: [
        { name: 'Alex', avatar: '🤖', primaryModel: 'Opus 4', adoption: 94, sessionsThisWeek: 28, avgAcceptance: 84 },
        { name: 'You', avatar: '👾', primaryModel: 'Sonnet 4', adoption: 87, sessionsThisWeek: 23, avgAcceptance: 78 },
        { name: 'Morgan', avatar: '🎨', primaryModel: 'Sonnet 4', adoption: 82, sessionsThisWeek: 25, avgAcceptance: 76 },
        { name: 'Sam', avatar: '🚀', primaryModel: 'Sonnet 4', adoption: 78, sessionsThisWeek: 19, avgAcceptance: 73 },
        { name: 'Casey', avatar: '🎯', primaryModel: 'Haiku 4', adoption: 95, sessionsThisWeek: 31, avgAcceptance: 65 },
        { name: 'Blake', avatar: '🎪', primaryModel: 'Haiku 4', adoption: 88, sessionsThisWeek: 16, avgAcceptance: 62 },
      ],
      adoptionTrend: [
        { week: 'W1', sonnet: 52, opus: 32, haiku: 8 },
        { week: 'W2', sonnet: 55, opus: 30, haiku: 10 },
        { week: 'W3', sonnet: 56, opus: 29, haiku: 12 },
        { week: 'W4', sonnet: 58, opus: 28, haiku: 14 },
      ],
      modelSwitching: [
        { from: 'Haiku', to: 'Sonnet', reason: 'complexity', count: 23 },
        { from: 'Sonnet', to: 'Opus', reason: 'quality', count: 15 },
        { from: 'Opus', to: 'Sonnet', reason: 'cost', count: 8 },
      ],
    },

    // People + Agent collaboration insights
    collaboration: {
      topPairs: [
        { person: 'Alex 🤖', model: 'Opus 4', sessions: 42, synergy: 94, acceptance: 89 },
        { person: 'Morgan 🎨', model: 'Sonnet 4', sessions: 38, synergy: 88, acceptance: 82 },
        { person: 'You 👾', model: 'Sonnet 4', sessions: 35, synergy: 85, acceptance: 78 },
      ],
      collaborationPatterns: {
        deepWork: { model: 'Opus 4', avgDuration: 52, tasks: ['refactoring', 'architecture'] },
        iterative: { model: 'Sonnet 4', avgDuration: 28, tasks: ['feature-dev', 'debugging'] },
        quick: { model: 'Haiku 4', avgDuration: 12, tasks: ['doc-updates', 'minor-fixes'] },
      },
      toolUsageByModel: {
        'Opus 4': { Edit: 42, Write: 28, Bash: 15, Read: 10 },
        'Sonnet 4': { Edit: 38, Write: 22, Bash: 18, Read: 14 },
        'Haiku 4': { Edit: 25, Write: 35, Bash: 20, Read: 18 },
      },
    },
  },

  // Live data (real-time-ish)
  live: {
    currentSession: {
      active: true,
      duration: 18, // minutes
      tokens: 45678,
      model: 'claude-sonnet-4',
      tools: ['Read', 'Edit', 'Read', 'Bash'],
    },
    activeUsers: 7,
    todaySpend: 127.50,
    recentActivity: [
      { time: '2m ago', user: 'Alex 👾', action: 'created PR', target: '#234' },
      { time: '5m ago', user: 'Jordan 🦊', action: 'merged PR', target: '#231' },
      { time: '8m ago', user: 'You', action: 'accepted edit', target: 'Auth.tsx' },
      { time: '12m ago', user: 'Sam 🚀', action: 'started session', target: '' },
    ]
  }
};

// =============================================================================
// STYLED COMPONENTS - Pixel Art Style
// =============================================================================

// Pixel border utility - creates chunky pixel-perfect borders
const pixelBorder = (color = theme.colors.border, width = 2) => ({
  boxShadow: `
    ${width}px 0 0 ${color},
    -${width}px 0 0 ${color},
    0 ${width}px 0 ${color},
    0 -${width}px 0 ${color}
  `,
});

// CRT scanline effect
const CRTOverlay = () => (
  <style>{`
    .crt::before {
      content: '';
      position: absolute;
      inset: 0;
      background: repeating-linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.15) 0px,
        rgba(0, 0, 0, 0.15) 1px,
        transparent 1px,
        transparent 2px
      );
      pointer-events: none;
      z-index: 1;
    }
  `}</style>
);

// Pixel card component
function PixelCard({ children, className = '', hover = true, glow = false, style }) {
  return (
    <div
      className={`pixel-card ${className}`}
      style={{
        backgroundColor: theme.colors.card,
        border: '2px solid',
        borderColor: theme.colors.border,
        position: 'relative',
        transition: 'all 0.15s steps(2)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...(glow && {
          boxShadow: `0 0 20px ${theme.colors.primaryDim}, 0 0 40px ${theme.colors.primaryDim}33`,
        }),
        ...style,
      }}
    >
      {children}
      <style>{`
        .pixel-card:hover {
          background-color: ${hover ? theme.colors.cardHover : 'transparent'} !important;
          border-color: ${hover ? theme.colors.borderLight : theme.colors.border} !important;
          transform: ${hover ? 'translate(-2px, -2px)' : 'none'};
          box-shadow: ${hover ? '4px 4px 0 rgba(0,0,0,0.3)' : 'none'};
        }
      `}</style>
    </div>
  );
}

// Pixel button component
function PixelButton({ children, onClick, variant = 'primary', size = 'md', disabled = false }) {
  const variants = {
    primary: {
      bg: theme.colors.primary,
      text: theme.colors.bg,
      border: theme.colors.primary,
    },
    secondary: {
      bg: theme.colors.secondary,
      text: theme.colors.bg,
      border: theme.colors.secondary,
    },
    outline: {
      bg: 'transparent',
      text: theme.colors.text,
      border: theme.colors.borderLight,
    },
    ghost: {
      bg: 'transparent',
      text: theme.colors.textMuted,
      border: 'transparent',
    }
  };

  const sizes = {
    sm: { padding: '6px 12px', fontSize: '14px' },
    md: { padding: '10px 20px', fontSize: '16px' },
    lg: { padding: '14px 28px', fontSize: '18px' },
  };

  const v = variants[variant];
  const s = sizes[size];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: theme.fonts.header,
        fontSize: s.fontSize,
        padding: s.padding,
        backgroundColor: v.bg,
        color: v.text,
        border: `2px solid ${v.border}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.1s steps(1)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}
      className="pixel-btn"
    >
      {children}
      <style>{`
        .pixel-btn:hover:not(:disabled) {
          transform: translate(-2px, -2px);
          box-shadow: 3px 3px 0 ${variant === 'outline' ? theme.colors.borderLight : v.border};
        }
        .pixel-btn:active:not(:disabled) {
          transform: translate(0, 0);
          box-shadow: none;
        }
      `}</style>
    </button>
  );
}

// Stat badge component
function StatBadge({ value, label, trend, icon: Icon, color = 'primary' }) {
  const colors = {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    success: theme.colors.success,
    warning: theme.colors.warning,
    danger: theme.colors.danger,
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {Icon && (
        <div style={{
          color: colors[color],
          display: 'flex',
          alignItems: 'center',
        }}>
          <Icon />
        </div>
      )}
      <div>
        <div style={{
          fontFamily: theme.fonts.header,
          fontSize: '24px',
          color: colors[color],
          lineHeight: 1.2,
        }}>
          {value}
        </div>
        <div style={{
          fontFamily: theme.fonts.body,
          fontSize: '16px',
          color: theme.colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          {label}
        </div>
      </div>
      {trend && (
        <div style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: trend.startsWith('+') ? theme.colors.success : theme.colors.danger,
          marginLeft: 'auto',
        }}>
          {trend}
        </div>
      )}
    </div>
  );
}

// Progress bar with pixel style
function PixelProgressBar({ value, max = 100, color = 'primary', label }) {
  const colors = {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    success: theme.colors.success,
    warning: theme.colors.warning,
    danger: theme.colors.danger,
  };

  const percent = Math.min(100, Math.max(0, (value / max) * 100));
  const c = colors[color];

  return (
    <div>
      {label && (
        <div style={{
          fontFamily: theme.fonts.body,
          fontSize: '16px',
          color: theme.colors.textMuted,
          marginBottom: '6px',
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <span>{label}</span>
          <span>{value}/{max}</span>
        </div>
      )}
      <div style={{
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
        height: '16px',
        position: 'relative',
      }}>
        <div style={{
          backgroundColor: c,
          width: `${percent}%`,
          height: '100%',
          transition: 'width 0.3s steps(10)',
        }} />
      </div>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

// Header with nav and role toggle
function Header({ userRole, timeRange }) {
  return (
    <header style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      borderBottom: `2px solid ${theme.colors.border}`,
      background: `linear-gradient(180deg, ${theme.colors.card} 0%, ${theme.colors.bg} 100%)`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Link to="/dashboard" style={{
          color: theme.colors.textMuted,
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: theme.fonts.body,
          fontSize: '18px',
        }}>
          ← BACK
        </Link>
        <div style={{
          width: '2px',
          height: '24px',
          backgroundColor: theme.colors.border
        }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '28px' }}>📊</span>
            <h1 style={{
              fontFamily: theme.fonts.header,
              fontSize: '18px',
              color: theme.colors.text,
              margin: 0,
            }}>
              LEDGER TELEMETRY
            </h1>
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textDim,
            marginLeft: '40px',
          }}>
            {userRole === 'admin'
              ? 'People + Agent Management Dashboard'
              : 'Your Performance Metrics'
            }
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Benchmark link */}
        <Link to="/benchmark" style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          padding: '8px 16px',
          background: 'transparent',
          color: theme.colors.secondary,
          border: `2px solid ${theme.colors.secondary}`,
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          ⚡ BENCHMARK
        </Link>

        {/* Role toggle - now uses Link navigation */}
        <div style={{
          display: 'flex',
          border: `2px solid ${theme.colors.border}`,
          borderRadius: 0,
        }}>
          <Link
            to="/otel/personal"
            style={{
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              padding: '8px 16px',
              background: userRole === 'personal' ? theme.colors.primary : 'transparent',
              color: userRole === 'personal' ? theme.colors.bg : theme.colors.textMuted,
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            👤 PERSONAL
          </Link>
          <Link
            to="/otel/admin"
            style={{
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              padding: '8px 16px',
              background: userRole === 'admin' ? theme.colors.secondary : 'transparent',
              color: userRole === 'admin' ? theme.colors.bg : theme.colors.textMuted,
              border: 'none',
              borderLeft: `2px solid ${theme.colors.border}`,
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            👑 ADMIN
          </Link>
        </div>
      </div>
    </header>
  );
}

// Live activity ticker
function LiveTicker({ live }) {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '24px',
      padding: '12px 24px',
      backgroundColor: theme.colors.card,
      borderBottom: `1px solid ${theme.colors.border}`,
    }}>
      {/* Live indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{
          display: 'inline-block',
          width: '8px',
          height: '8px',
          backgroundColor: theme.colors.success,
          animation: 'pulse 1s infinite',
        }} />
        <span style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.success,
        }}>
          LIVE
        </span>
      </div>

      {/* Quick stats */}
      <div style={{
        display: 'flex',
        gap: '24px',
        fontFamily: theme.fonts.body,
        fontSize: '18px',
        color: theme.colors.text,
      }}>
        <span>
          <span style={{ color: theme.colors.textMuted }}>ACTIVE:</span>{' '}
          <span style={{ color: theme.colors.primary }}>{live.activeUsers}</span>
        </span>
        <span>
          <span style={{ color: theme.colors.textMuted }}>TODAY:</span>{' '}
          <span style={{ color: theme.colors.secondary }}>${live.todaySpend.toFixed(2)}</span>
        </span>
        {live.currentSession.active && (
          <span>
            <span style={{ color: theme.colors.textMuted }}>SESSION:</span>{' '}
            <span style={{ color: theme.colors.success }}>{live.currentSession.duration}m</span>
            {' '}·{' '}
            <span>{(live.currentSession.tokens / 1000).toFixed(1)}k tokens</span>
          </span>
        )}
      </div>

      {/* Clock */}
      <div style={{
        marginLeft: 'auto',
        fontFamily: theme.fonts.body,
        fontSize: '18px',
        color: theme.colors.textMuted,
      }}>
        {currentTime.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

// Quality score card
function QualityScoreCard({ data }) {
  return (
    <PixelCard className="p-6" glow={data.qualityScore.current >= 90}>
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>
          CODE QUALITY SCORE
        </h2>
      </div>

      {/* Main score display */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
        marginBottom: '16px',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '64px',
            color: data.qualityScore.current >= 90 ? theme.colors.primary :
                   data.qualityScore.current >= 75 ? theme.colors.success :
                   data.qualityScore.current >= 60 ? theme.colors.warning :
                   theme.colors.danger,
            lineHeight: 1,
            marginBottom: '8px',
          }}>
            {data.qualityScore.current}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '16px',
            color: theme.colors.textMuted,
          }}>
            {data.qualityScore.current >= 90 ? 'EXCELLENT' :
             data.qualityScore.current >= 75 ? 'GOOD' :
             data.qualityScore.current >= 60 ? 'FAIR' : 'NEEDS WORK'}
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <PixelProgressBar
          value={data.qualityScore.breakdown.toolAcceptance}
          label="Tool Acceptance"
          color={data.qualityScore.breakdown.toolAcceptance >= 80 ? 'success' : 'primary'}
        />
        <PixelProgressBar
          value={100 - data.qualityScore.breakdown.errorRate}
          label="Error-Free Rate"
          color="success"
        />
        <PixelProgressBar
          value={data.qualityScore.breakdown.codeReviewPass}
          label="Review Pass Rate"
          color="primary"
        />
        <PixelProgressBar
          value={data.qualityScore.breakdown.testCoverage}
          label="Test Coverage"
          color="secondary"
        />
      </div>
    </PixelCard>
  );
}

// Token efficiency card
function TokenEfficiencyCard({ data, team }) {
  const percentile = team.percentiles;
  const yourRatio = data.tokenEfficiency.ratio;

  // Determine percentile
  let userPercentile = 'p25';
  if (yourRatio <= percentile.p90.ratio) userPercentile = 'p90+';
  else if (yourRatio <= percentile.p75.ratio) userPercentile = 'p75';
  else if (yourRatio <= percentile.p50.ratio) userPercentile = 'p50';

  const chartData = [
    { metric: 'You', value: yourRatio, isYou: true },
    { metric: '25th', value: percentile.p25.ratio },
    { metric: '50th', value: percentile.p50.ratio },
    { metric: '75th', value: percentile.p75.ratio },
    { metric: '90th', value: percentile.p90.ratio },
  ];

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          TOKEN EFFICIENCY
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Tokens per line changed (lower is better)
        </p>
      </div>

      {/* Main ratio */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
        marginBottom: '16px',
      }}>
        <div>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '36px',
            color: theme.colors.secondary,
          }}>
            {yourRatio}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textMuted,
          }}>
            tokens/line
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '20px',
            color: userPercentile === 'p90+' ? theme.colors.success : theme.colors.primary,
          }}>
            Top {userPercentile === 'p90+' ? '10%' : userPercentile.replace('p', '') + '%'}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textDim,
          }}>
            vs team
          </div>
        </div>
      </div>

      {/* Comparison chart */}
      <div style={{ height: '120px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" barSize={20}>
            <XAxis
              type="number"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              type="category"
              dataKey="metric"
              style={{ fontFamily: theme.fonts.body, fontSize: '12px' }}
              stroke={theme.colors.textDim}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.colors.card,
                border: `2px solid ${theme.colors.border}`,
                fontFamily: theme.fonts.body,
              }}
              formatter={(value) => [value, 'tokens/line']}
            />
            <Bar
              dataKey="value"
              fill={theme.colors.primary}
              radius={[0, 0, 0, 0]}
              shape={(props) => {
                const { x, y, width, height, payload } = props;
                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={payload.isYou ? theme.colors.secondary : theme.colors.primary}
                    opacity={payload.isYou ? 1 : 0.6}
                  />
                );
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Model breakdown */}
      <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${theme.colors.border}` }}>
        <div style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>
          BY MODEL:
        </div>
        {Object.entries(data.tokenEfficiency.byModel).map(([model, stats]) => (
          <div key={model} style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: theme.fonts.body,
            fontSize: '16px',
            padding: '4px 0',
          }}>
            <span style={{ color: theme.colors.textMuted }}>
              {model.split('-')[1]?.toUpperCase() || model}
            </span>
            <span style={{ color: theme.colors.text }}>
              {stats.ratio} <span style={{ color: theme.colors.textDim }}>(T)</span>
            </span>
          </div>
        ))}
      </div>
    </PixelCard>
  );
}

// PR efficiency card
function PREfficiencyCard({ data }) {
  const chartData = data.sessions.weeklyData.map(d => ({
    day: d.day.substring(0, 3),
    PRs: Math.max(1, Math.round(d.sessions / 2)), // Simulated PR count based on sessions
    merged: Math.max(1, Math.round(d.sessions / 2 * data.prEfficiency.mergeRate / 100)),
  }));

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          PR EFFICIENCY
        </h2>
      </div>

      {/* Stats grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        marginBottom: '16px',
      }}>
        <div style={{
          padding: '12px',
          backgroundColor: theme.colors.bg,
          border: `2px solid ${theme.colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '24px',
            color: theme.colors.primary,
          }}>
            {data.prEfficiency.created}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
          }}>
            CREATED
          </div>
        </div>
        <div style={{
          padding: '12px',
          backgroundColor: theme.colors.bg,
          border: `2px solid ${theme.colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '24px',
            color: theme.colors.success,
          }}>
            {data.prEfficiency.mergeRate}%
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
          }}>
            MERGE RATE
          </div>
        </div>
        <div style={{
          padding: '12px',
          backgroundColor: theme.colors.bg,
          border: `2px solid ${theme.colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '24px',
            color: theme.colors.secondary,
          }}>
            {data.prEfficiency.avgMergeTime}h
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
          }}>
            AVG MERGE
          </div>
        </div>
      </div>

      {/* AI contribution */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textMuted,
          marginBottom: '6px',
        }}>
          <span>AI Contribution to PRs</span>
          <span>{data.prEfficiency.aiContribution}%</span>
        </div>
        <div style={{
          height: '20px',
          backgroundColor: theme.colors.bg,
          border: `2px solid ${theme.colors.border}`,
        }}>
          <div style={{
            width: `${data.prEfficiency.aiContribution}%`,
            height: '100%',
            backgroundColor: theme.colors.primary,
          }} />
        </div>
      </div>

      {/* Weekly trend */}
      <div style={{ height: '80px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <XAxis
              dataKey="day"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.colors.card,
                border: `2px solid ${theme.colors.border}`,
                fontFamily: theme.fonts.body,
              }}
            />
            <Line
              type="stepAfter"
              dataKey="PRs"
              stroke={theme.colors.primary}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="stepAfter"
              dataKey="merged"
              stroke={theme.colors.success}
              strokeWidth={2}
              dot={false}
              strokeDasharray="4 4"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* PR Size Distribution */}
      <div style={{
        marginTop: '12px',
        padding: '10px',
        backgroundColor: theme.colors.bg,
        border: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{
          fontFamily: theme.fonts.body,
          fontSize: '12px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>
          PR SIZE DISTRIBUTION (merge time)
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {data.prEfficiency.sizeDistribution.map((size, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: theme.fonts.body,
              fontSize: '14px',
            }}>
              <span style={{
                width: '70px',
                color: theme.colors.textMuted,
                fontSize: '12px',
              }}>{size.size}</span>
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <div style={{
                  flex: 1,
                  height: '8px',
                  backgroundColor: theme.colors.card,
                  border: `1px solid ${theme.colors.border}`,
                  position: 'relative',
                }}>
                  <div style={{
                    width: `${(size.count / data.prEfficiency.created) * 100}%`,
                    height: '100%',
                    backgroundColor: size.count > 5 ? theme.colors.secondary :
                                   size.count > 2 ? theme.colors.primary :
                                   theme.colors.success,
                  }} />
                </div>
                <span style={{
                  minWidth: '20px',
                  fontSize: '12px',
                  color: theme.colors.textMuted,
                  textAlign: 'right',
                }}>{size.count}</span>
                <span style={{
                  fontSize: '12px',
                  color: size.avgMergeTime < 4 ? theme.colors.success :
                         size.avgMergeTime < 8 ? theme.colors.primary :
                         theme.colors.warning,
                  }}>{size.avgMergeTime}h</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '8px',
        fontFamily: theme.fonts.body,
        fontSize: '12px',
      }}>
        <span style={{ color: theme.colors.textMuted }}>
          <span style={{ color: theme.colors.primary }}>━</span> Created
        </span>
        <span style={{ color: theme.colors.textMuted }}>
          <span style={{ color: theme.colors.success }}>---</span> Merged
        </span>
      </div>
    </PixelCard>
  );
}

// Productivity heatmap
function ProductivityHeatmap({ data }) {
  const max = Math.max(...data.productivityHeatmap.data.flat());

  const getColor = (value) => {
    const intensity = value / max;
    if (intensity < 0.25) return 'rgba(100, 255, 218, 0.1)';
    if (intensity < 0.5) return 'rgba(100, 255, 218, 0.3)';
    if (intensity < 0.75) return 'rgba(100, 255, 218, 0.6)';
    return 'rgba(100, 255, 218, 1)';
  };

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          PRODUCTIVITY HEATMAP
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Activity intensity by hour and day
        </p>
      </div>

      {/* Heatmap grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `auto repeat(${data.productivityHeatmap.labels.length}, 1fr)`,
        gap: '4px',
      }}>
        {/* Hour labels */}
        <div></div>
        {data.productivityHeatmap.labels.map((day, i) => (
          <div key={i} style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
            textAlign: 'center',
          }}>
            {day.substring(0, 3)}
          </div>
        ))}

        {/* Data rows */}
        {data.productivityHeatmap.data.map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '12px',
              color: theme.colors.textMuted,
              textAlign: 'right',
              paddingRight: '8px',
            }}>
              {data.productivityHeatmap.hours[rowIndex]}
            </div>
            {row.map((value, colIndex) => (
              <div
                key={colIndex}
                style={{
                  aspectRatio: 1,
                  backgroundColor: getColor(value),
                  border: `1px solid ${theme.colors.border}`,
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.1s',
                }}
                title={`${data.productivityHeatmap.labels[colIndex]} ${data.productivityHeatmap.hours[rowIndex]}: ${value}%`}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.2)';
                  e.target.style.zIndex = '10';
                  e.target.style.boxShadow = '0 0 10px rgba(100, 255, 218, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.zIndex = '1';
                  e.target.style.boxShadow = 'none';
                }}
              />
            ))}
          </React.Fragment>
        ))}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '4px',
        marginTop: '12px',
      }}>
        <span style={{
          fontFamily: theme.fonts.body,
          fontSize: '12px',
          color: theme.colors.textDim,
          marginRight: '8px',
        }}>Low</span>
        {[0.1, 0.3, 0.6, 1].map(opacity => (
          <div
            key={opacity}
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: `rgba(100, 255, 218, ${opacity})`,
              border: `1px solid ${theme.colors.border}`,
            }}
          />
        ))}
        <span style={{
          fontFamily: theme.fonts.body,
          fontSize: '12px',
          color: theme.colors.textDim,
          marginLeft: '8px',
        }}>High</span>
      </div>
    </PixelCard>
  );
}

// Tool acceptance radar
function ToolAcceptanceCard({ data }) {
  const radarData = Object.entries(data.toolAcceptance.byTool).map(([tool, rate]) => ({
    tool,
    rate,
    fullMark: 100,
  }));

  const COLORS = [theme.colors.primary, theme.colors.secondary, theme.colors.success, theme.colors.warning, theme.colors.accent, theme.colors.danger];

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          TOOL ACCEPTANCE RATE
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          When do you accept Claude's suggestions?
        </p>
      </div>

      {/* Overall rate */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
        marginBottom: '16px',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '48px',
            color: data.toolAcceptance.overall >= 75 ? theme.colors.success :
                   data.toolAcceptance.overall >= 60 ? theme.colors.primary :
                   theme.colors.warning,
          }}>
            {data.toolAcceptance.overall}%
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textMuted,
          }}>
            Overall Acceptance
          </div>
        </div>
      </div>

      {/* Tool breakdown */}
      <div style={{ marginBottom: '16px' }}>
        {Object.entries(data.toolAcceptance.byTool).map(([tool, rate], i) => (
          <div key={tool} style={{ marginBottom: '8px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              color: theme.colors.textMuted,
              marginBottom: '4px',
            }}>
              <span>{tool}</span>
              <span style={{ color: rate >= 75 ? theme.colors.success : rate >= 60 ? theme.colors.primary : theme.colors.warning }}>
                {rate}%
              </span>
            </div>
            <div style={{
              height: '8px',
              backgroundColor: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                width: `${rate}%`,
                height: '100%',
                backgroundColor: COLORS[i % COLORS.length],
              }} />
            </div>
          </div>
        ))}
      </div>

      {/* Trend chart */}
      <div style={{ height: '80px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.toolAcceptance.trend}>
            <XAxis
              dataKey="week"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              domain={[60, 85]}
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <Line
              type="monotone"
              dataKey="rate"
              stroke={theme.colors.primary}
              strokeWidth={2}
              dot={{ fill: theme.colors.primary, r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </PixelCard>
  );
}

// Team leaderboard (anonymized)
function TeamLeaderboard({ team, userRole, adminData }) {
  const displayData = userRole === 'admin' ? adminData.fullTeam : team.leaderboard;

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          {userRole === 'admin' ? 'TEAM LEADERBOARD' : 'ANONYMIZED RANKINGS'}
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          {team.totalMembers} team members • {team.activeNow} active now
        </p>
      </div>

      {/* Leaderboard */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {displayData.map((member, i) => (
          <div
            key={member.id || member.rank}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              backgroundColor: member.isYou ? theme.colors.primaryDim : theme.colors.bg,
              border: `2px solid ${member.isYou ? theme.colors.primary : theme.colors.border}`,
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!member.isYou) {
                e.currentTarget.style.backgroundColor = theme.colors.cardHover;
                e.currentTarget.style.borderColor = theme.colors.borderLight;
              }
            }}
            onMouseLeave={(e) => {
              if (!member.isYou) {
                e.currentTarget.style.backgroundColor = theme.colors.bg;
                e.currentTarget.style.borderColor = theme.colors.border;
              }
            }}
          >
            {/* Rank */}
            <div style={{
              fontFamily: theme.fonts.header,
              fontSize: i < 3 ? '16px' : '12px',
              color: i === 0 ? theme.colors.secondary :
                     i === 1 ? theme.colors.textMuted :
                     i === 2 ? theme.colors.warning :
                     theme.colors.textDim,
              width: '24px',
              textAlign: 'center',
            }}>
              {i < 3 ? ['🥇', '🥈', '🥉'][i] : member.rank || i + 1}
            </div>

            {/* Avatar */}
            <div style={{ fontSize: '24px' }}>
              {member.avatar}
            </div>

            {/* Name */}
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '18px',
              color: member.isYou ? theme.colors.primary : theme.colors.text,
              flex: 1,
            }}>
              {member.name}
              {member.isYou && <span style={{
                marginLeft: '8px',
                fontSize: '12px',
                color: theme.colors.primary,
              }}> (YOU)</span>}
            </div>

            {/* Metrics - different for admin vs personal */}
            <div style={{ textAlign: 'right' }}>
              {userRole === 'admin' ? (
                <>
                  {/* Admin mode: Show tokens, sessions, acceptance */}
                  <div style={{
                    fontFamily: theme.fonts.header,
                    fontSize: '14px',
                    color: theme.colors.secondary,
                  }}>
                    {(member.tokens / 1000000).toFixed(2)}M tokens
                  </div>
                  <div style={{
                    fontFamily: theme.fonts.body,
                    fontSize: '11px',
                    color: theme.colors.textDim,
                  }}>
                    {member.acceptance || 78}% accept
                  </div>
                </>
              ) : (
                <>
                  {/* Personal mode: Show score and PRs */}
                  <div style={{
                    fontFamily: theme.fonts.header,
                    fontSize: '16px',
                    color: member.score >= 85 ? theme.colors.primary :
                           member.score >= 70 ? theme.colors.success :
                           theme.colors.warning,
                  }}>
                    {member.score}
                  </div>
                  <div style={{
                    fontFamily: theme.fonts.body,
                    fontSize: '12px',
                    color: theme.colors.textDim,
                  }}>
                    {member.prs} PRs
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Admin-only cost breakdown */}
      {userRole === 'admin' && adminData.costs && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `2px solid ${theme.colors.border}` }}>
          <h3 style={{
            fontFamily: theme.fonts.header,
            fontSize: '10px',
            color: theme.colors.textMuted,
            marginBottom: '12px',
          }}>
            MONTHLY COST PER PERSON
          </h3>
          {adminData.costs.byPerson.map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              fontFamily: theme.fonts.body,
              fontSize: '16px',
            }}>
              <span style={{ color: theme.colors.text }}>
                {item.avatar} {item.name}
              </span>
              <span style={{
                color: item.month > 400 ? theme.colors.warning :
                       item.month > 300 ? theme.colors.primary :
                       theme.colors.success,
              }}>
                ${item.month.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </PixelCard>
  );
}

// Model comparison card
function ModelComparisonCard({ team }) {
  const chartData = team.modelDistribution.map(m => ({
    model: m.model.split(' ')[0],
    usage: m.usage,
    quality: m.avgQuality,
  }));

  const COLORS = {
    Sonnet: theme.colors.primary,
    Opus: theme.colors.secondary,
    Haiku: theme.colors.success,
  };

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          MODEL COMPARISON
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Usage vs Quality tradeoffs
        </p>
      </div>

      {/* Dual-axis chart */}
      <div style={{ height: '150px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barSize={40}>
            <XAxis
              dataKey="model"
              style={{ fontFamily: theme.fonts.body, fontSize: '12px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, 100]}
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.colors.card,
                border: `2px solid ${theme.colors.border}`,
                fontFamily: theme.fonts.body,
              }}
            />
            <Bar
              yAxisId="left"
              dataKey="usage"
              fill={theme.colors.primary}
              radius={[0, 0, 0, 0]}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="quality"
              stroke={theme.colors.secondary}
              strokeWidth={3}
              dot={{ fill: theme.colors.secondary, r: 5 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginTop: '8px',
        fontFamily: theme.fonts.body,
        fontSize: '12px',
      }}>
        <span style={{ color: theme.colors.textMuted }}>
          <span style={{
            display: 'inline-block',
            width: '12px',
            height: '12px',
            backgroundColor: theme.colors.primary,
            marginRight: '4px',
          }}></span>
          Usage %
        </span>
        <span style={{ color: theme.colors.textMuted }}>
          <span style={{
            display: 'inline-block',
            width: '12px',
            height: '12px',
            border: `2px solid ${theme.colors.secondary}`,
            marginRight: '4px',
          }}></span>
          Quality
        </span>
      </div>

      {/* Insight */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
        fontFamily: theme.fonts.body,
        fontSize: '14px',
        color: theme.colors.textMuted,
      }}>
        💡 Team uses <span style={{ color: theme.colors.primary }}>Sonnet</span> most -
        best balance of quality and cost.
        <span style={{ color: theme.colors.secondary }}> Opus</span> yields
        highest quality scores but costs 3x more.
      </div>
    </PixelCard>
  );
}

// Reversion rate card - tracks how much of your code gets reverted/modified downstream
function ReversionRateCard({ data, team }) {
  const reversion = data.reversionRate;
  const isImproving = reversion.trend === 'improving';
  const isBetterThanTeam = reversion.overall < reversion.teamAverage;

  // Chart data for time since merge
  const timeData = reversion.byTimeSinceMerge.map(d => ({
    name: d.week,
    Reverted: d.reverted,
    Modified: d.modified,
  }));

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <h2 style={{
            fontFamily: theme.fonts.header,
            fontSize: '12px',
            color: theme.colors.textMuted,
          }}>
            REVERSION RATE
          </h2>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            padding: '4px 8px',
            backgroundColor: isBetterThanTeam ? theme.colors.success + '20' : theme.colors.warning + '20',
            border: `1px solid ${isBetterThanTeam ? theme.colors.success : theme.colors.warning}`,
            color: isBetterThanTeam ? theme.colors.success : theme.colors.warning,
          }}>
            {isBetterThanTeam ? '↓ BETTER THAN TEAM' : '↑ BELOW TEAM AVG'}
          </div>
        </div>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Code reverted or heavily modified downstream
        </p>
      </div>

      {/* Main rate display */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
        marginBottom: '16px',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '48px',
            color: reversion.overall <= 5 ? theme.colors.success :
                   reversion.overall <= 10 ? theme.colors.primary :
                   reversion.overall <= 15 ? theme.colors.warning :
                   theme.colors.danger,
          }}>
            {reversion.overall}%
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textMuted,
          }}>
            Overall Reversion
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: isImproving ? theme.colors.success : theme.colors.danger,
            marginTop: '4px',
          }}>
            {isImproving ? '↓ Improving' : '↑ Worsening'} vs {reversion.teamAverage}% team avg
          </div>
        </div>
      </div>

      {/* Breakdown bars */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>
          BREAKDOWN:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '12px',
              width: '100px',
              color: theme.colors.textMuted,
            }}>Untouched</div>
            <div style={{
              flex: 1,
              height: '12px',
              backgroundColor: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`,
              position: 'relative',
            }}>
              <div style={{
                width: `${reversion.breakdown.untouched}%`,
                height: '100%',
                backgroundColor: theme.colors.success,
              }} />
            </div>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              width: '35px',
              textAlign: 'right',
              color: theme.colors.success,
            }}>{reversion.breakdown.untouched}%</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '12px',
              width: '100px',
              color: theme.colors.textMuted,
            }}>Light edits</div>
            <div style={{
              flex: 1,
              height: '12px',
              backgroundColor: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                width: `${reversion.breakdown.lightlyTouched}%`,
                height: '100%',
                backgroundColor: theme.colors.primary,
              }} />
            </div>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              width: '35px',
              textAlign: 'right',
              color: theme.colors.primary,
            }}>{reversion.breakdown.lightlyTouched}%</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '12px',
              width: '100px',
              color: theme.colors.textMuted,
            }}>Heavy mod</div>
            <div style={{
              flex: 1,
              height: '12px',
              backgroundColor: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                width: `${reversion.breakdown.heavilyModified}%`,
                height: '100%',
                backgroundColor: theme.colors.warning,
              }} />
            </div>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              width: '35px',
              textAlign: 'right',
              color: theme.colors.warning,
            }}>{reversion.breakdown.heavilyModified}%</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '12px',
              width: '100px',
              color: theme.colors.textMuted,
            }}>Reverted</div>
            <div style={{
              flex: 1,
              height: '12px',
              backgroundColor: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                width: `${reversion.breakdown.fullyReverted}%`,
                height: '100%',
                backgroundColor: theme.colors.danger,
              }} />
            </div>
            <div style={{
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              width: '35px',
              textAlign: 'right',
              color: theme.colors.danger,
            }}>{reversion.breakdown.fullyReverted}%</div>
          </div>
        </div>
      </div>

      {/* Time since merge chart */}
      <div style={{ height: '100px', marginBottom: '12px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={timeData} barSize={20} layout="vertical">
            <XAxis
              type="number"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              type="category"
              dataKey="name"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
              width={50}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.colors.card,
                border: `2px solid ${theme.colors.border}`,
                fontFamily: theme.fonts.body,
              }}
              formatter={(value) => [`${value}%`, '']}
            />
            <Bar dataKey="Reverted" fill={theme.colors.danger} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Modified" fill={theme.colors.warning} radius={[0, 0, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top churn files */}
      <div style={{
        padding: '10px',
        backgroundColor: theme.colors.bg,
        border: `1px solid ${theme.colors.border}`,
        fontFamily: theme.fonts.body,
        fontSize: '12px',
      }}>
        <div style={{ color: theme.colors.textMuted, marginBottom: '6px' }}>HIGHEST CHURN:</div>
        {reversion.topFiles.map((f, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '2px 0',
            color: f.churn > 20 ? theme.colors.warning : theme.colors.text,
          }}>
            <span>{f.file}</span>
            <span>{f.churn}% churn</span>
          </div>
        ))}
      </div>
    </PixelCard>
  );
}

// Session activity card
function SessionActivityCard({ data }) {
  const chartData = data.sessions.weeklyData;

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          SESSION ACTIVITY
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          {data.sessions.total} sessions • {data.sessions.streak} day streak
        </p>
      </div>

      {/* Streak badge */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '16px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: theme.colors.secondaryDim,
          border: `2px solid ${theme.colors.secondary}`,
        }}>
          <span style={{ fontSize: '20px' }}>🔥</span>
          <span style={{
            fontFamily: theme.fonts.header,
            fontSize: '14px',
            color: theme.colors.secondary,
          }}>
            {data.sessions.streak} DAY STREAK
          </span>
        </div>
      </div>

      {/* Activity chart */}
      <div style={{ height: '140px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <XAxis
              dataKey="day"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.colors.card,
                border: `2px solid ${theme.colors.border}`,
                fontFamily: theme.fonts.body,
              }}
            />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="sessions"
              stroke={theme.colors.primary}
              fill={theme.colors.primary}
              fillOpacity={0.3}
            />
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="tokens"
              stroke={theme.colors.secondary}
              fill={theme.colors.secondary}
              fillOpacity={0.3}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginTop: '8px',
        fontFamily: theme.fonts.body,
        fontSize: '12px',
      }}>
        <span style={{ color: theme.colors.textMuted }}>
          <span style={{ color: theme.colors.primary }}>■</span> Sessions
        </span>
        <span style={{ color: theme.colors.textMuted }}>
          <span style={{ color: theme.colors.secondary }}>■</span> Tokens (k)
        </span>
      </div>

      {/* Peak time */}
      <div style={{
        marginTop: '12px',
        padding: '10px',
        backgroundColor: theme.colors.bg,
        border: `1px solid ${theme.colors.border}`,
        fontFamily: theme.fonts.body,
        fontSize: '14px',
        textAlign: 'center',
        color: theme.colors.textMuted,
      }}>
        Most productive: <span style={{ color: theme.colors.primary }}>
          {data.sessions.mostActiveDay}s at {data.sessions.mostActiveHour}:00
        </span>
      </div>
    </PixelCard>
  );
}

// =============================================================================
// TIME RANGE DATA ADAPTER
// =============================================================================

// Multipliers for different time ranges to make data vary realistically
const timeRangeMultipliers = {
  live: { sessions: 0.01, prs: 0.01, tokens: 0.001, activity: 0.1 },
  week: { sessions: 1, prs: 1, tokens: 1, activity: 1 },
  month: { sessions: 4, prs: 4.2, tokens: 4.3, activity: 1 },
  quarter: { sessions: 12, prs: 13, tokens: 13.5, activity: 1 },
};

function getDataByTimeRange(range, baseData) {
  const mult = timeRangeMultipliers[range] || timeRangeMultipliers.week;

  // Deep clone and scale data
  return {
    ...baseData,
    personal: {
      ...baseData.personal,
      qualityScore: {
        ...baseData.personal.qualityScore,
        current: range === 'live' ? 78 : baseData.personal.qualityScore.current,
      },
      prEfficiency: {
        ...baseData.personal.prEfficiency,
        created: Math.round(baseData.personal.prEfficiency.created * (range === 'live' ? 0.05 : mult.prs)),
        merged: Math.round(baseData.personal.prEfficiency.merged * (range === 'live' ? 0.05 : mult.prs)),
      },
      tokenEfficiency: {
        ...baseData.personal.tokenEfficiency,
        totalTokens: Math.round(baseData.personal.tokenEfficiency.totalTokens * mult.tokens),
      },
      sessions: {
        ...baseData.personal.sessions,
        total: Math.round(baseData.personal.sessions.total * mult.sessions),
      },
    },
    team: {
      ...baseData.team,
      leaderboard: baseData.team.leaderboard.map(entry => ({
        ...entry,
        score: range === 'live' ? Math.round(entry.score * 0.8 + Math.random() * 10) : entry.score,
        prs: Math.round(entry.prs * (range === 'live' ? 0.02 : mult.prs)),
      })),
    },
    adminData: baseData.adminData ? {
      ...baseData.adminData,
      teamRoster: baseData.adminData.teamRoster?.map(member => ({
        ...member,
        currentSession: range === 'live' ? member.currentSession : null,
      })),
      costs: baseData.adminData.costs ? {
        ...baseData.adminData.costs,
        today: range === 'live' ? baseData.adminData.costs.today * 0.3 : baseData.adminData.costs.today * mult.tokens,
        week: baseData.adminData.costs.week * (range === 'month' ? 4.3 : range === 'quarter' ? 13 : 1),
        month: baseData.adminData.costs.month * (range === 'quarter' ? 3 : 1),
      } : null,
    } : null,
    // Live ticker always shows current data regardless of time range
    live: baseData.live,
  };
}

// Time range toggle
function TimeRangeToggle({ range, setRange }) {
  return (
    <div style={{
      display: 'flex',
      border: `2px solid ${theme.colors.border}`,
    }}>
      {['live', 'week', 'month', 'quarter'].map((r) => (
        <button
          key={r}
          onClick={() => setRange(r)}
          style={{
            fontFamily: theme.fonts.header,
            fontSize: '10px',
            padding: '8px 16px',
            backgroundColor: range === r ? theme.colors.primary : 'transparent',
            color: range === r ? theme.colors.bg : theme.colors.textMuted,
            border: 'none',
            borderRight: r !== 'quarter' ? `1px solid ${theme.colors.border}` : 'none',
            cursor: 'pointer',
            textTransform: 'uppercase',
          }}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

// =============================================================================
// ADMIN-ONLY COMPONENTS - People + Agent Focused
// =============================================================================

// Team Roster Card - shows who's working and what they're using
function TeamRosterCard({ data }) {
  const statusColors = {
    online: theme.colors.success,
    away: theme.colors.warning,
    offline: theme.colors.textDim,
  };

  const modelColors = {
    'Opus 4': theme.colors.secondary,
    'Sonnet 4': theme.colors.primary,
    'Haiku 4': theme.colors.success,
  };

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          👥 TEAM ROSTER
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Who's working + what agents they're using
        </p>
      </div>

      {/* Active users count */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '20px',
            color: theme.colors.success,
          }}>
            {data.filter(d => d.status === 'online').length}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textDim,
          }}>ONLINE</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '20px',
            color: theme.colors.warning,
          }}>
            {data.filter(d => d.status === 'away').length}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textDim,
          }}>AWAY</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '20px',
            color: theme.colors.textDim,
          }}>
            {data.filter(d => d.status === 'offline').length}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textDim,
          }}>OFFLINE</div>
        </div>
      </div>

      {/* Roster list */}
      <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
        {data.map((member) => (
          <div
            key={member.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px',
              marginBottom: '8px',
              backgroundColor: member.status === 'online' ? theme.colors.cardHover : 'transparent',
              border: `2px solid ${theme.colors.border}`,
              transition: 'all 0.1s',
            }}
          >
            {/* Status indicator */}
            <div style={{
              width: '8px',
              height: '8px',
              backgroundColor: statusColors[member.status],
              marginRight: '10px',
              boxShadow: member.status === 'online' ? `0 0 8px ${statusColors[member.status]}` : 'none',
            }} />

            {/* Avatar */}
            <span style={{ fontSize: '20px', marginRight: '10px' }}>{member.avatar}</span>

            {/* Name + Role */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '16px',
                color: theme.colors.text,
              }}>
                {member.name}
              </div>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '12px',
                color: theme.colors.textDim,
              }}>
                {member.role}
              </div>
            </div>

            {/* Current session info */}
            {member.currentSession ? (
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontFamily: theme.fonts.body,
                  fontSize: '12px',
                  color: modelColors[member.currentSession.model] || theme.colors.textMuted,
                }}>
                  {member.currentSession.model}
                </div>
                <div style={{
                  fontFamily: theme.fonts.body,
                  fontSize: '12px',
                  color: theme.colors.textDim,
                }}>
                  {member.currentSession.duration}m • {(member.currentSession.tokens / 1000).toFixed(0)}k tokens
                </div>
              </div>
            ) : (
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '12px',
                color: theme.colors.textDim,
                fontStyle: 'italic',
              }}>
                {member.status === 'away' ? 'AFK' : 'offline'}
              </div>
            )}
          </div>
        ))}
      </div>
    </PixelCard>
  );
}

// Cost Allocation Card - spending by person and model
function CostAllocationCard({ data }) {
  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          💰 COST ALLOCATION
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Spending by person + agent model
        </p>
      </div>

      {/* Period totals */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textDim,
          }}>TODAY</div>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '14px',
            color: theme.colors.primary,
          }}>${data.today.toFixed(2)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textDim,
          }}>WEEK</div>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '14px',
            color: theme.colors.primary,
          }}>${data.week.toFixed(2)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textDim,
          }}>MONTH</div>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '14px',
            color: theme.colors.secondary,
          }}>${data.month.toFixed(2)}</div>
        </div>
      </div>

      {/* By person chart */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>BY PERSON</h3>
        <div style={{ height: '120px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.byPerson.slice(0, 5)} layout="vertical">
              <XAxis
                type="number"
                style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
                stroke={theme.colors.textDim}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={60}
                style={{ fontFamily: theme.fonts.body, fontSize: '11px' }}
                stroke={theme.colors.textDim}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors.card,
                  border: `1px solid ${theme.colors.border}`,
                  fontFamily: theme.fonts.body,
                }}
                formatter={(value) => `$${value}`}
              />
              <Bar dataKey="month" fill={theme.colors.primary} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* By model */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>BY MODEL</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          {data.byModel.map((model) => (
            <div
              key={model.model}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: theme.colors.bg,
                border: `2px solid ${theme.colors.border}`,
                textAlign: 'center',
              }}
            >
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '12px',
                color: theme.colors.textMuted,
              }}>{model.model.split(' ')[0]}</div>
              <div style={{
                fontFamily: theme.fonts.header,
                fontSize: '14px',
                color: model.model.includes('Opus') ? theme.colors.secondary :
                       model.model.includes('Sonnet') ? theme.colors.primary :
                       theme.colors.success,
              }}>${model.cost.toFixed(0)}</div>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '10px',
                color: theme.colors.textDim,
              }}>{model.pct}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Forecast */}
      <div style={{
        padding: '10px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
        fontFamily: theme.fonts.body,
        fontSize: '12px',
        color: theme.colors.textMuted,
        textAlign: 'center',
      }}>
        📈 Forecast: <span style={{ color: theme.colors.primary }}>${data.forecast.nextWeek.toFixed(0)}</span> next week •
        <span style={{ color: theme.colors.secondary }}> ${data.forecast.nextMonth.toFixed(0)}</span> next month
      </div>
    </PixelCard>
  );
}

// Team Health Card - burnout risk, streaks, activity patterns
function TeamHealthCard({ data }) {
  const riskColors = {
    high: theme.colors.danger,
    medium: theme.colors.warning,
    low: theme.colors.success,
  };

  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          ❤️ TEAM HEALTH
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Burnout risk + activity patterns
        </p>
      </div>

      {/* Burnout alerts */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.danger,
          marginBottom: '8px',
        }}>⚠️ BURNOUT RISK</h3>
        {data.burnoutRisk.map((person) => (
          <div
            key={person.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              marginBottom: '6px',
              backgroundColor: person.risk === 'high' ? `${theme.colors.danger}11` : 'transparent',
              borderTop: `2px solid ${riskColors[person.risk]}`,
              borderRight: `2px solid ${riskColors[person.risk]}`,
              borderBottom: `2px solid ${riskColors[person.risk]}`,
              borderLeft: `4px solid ${riskColors[person.risk]}`,
            }}
          >
            <span style={{ fontSize: '16px', marginRight: '8px' }}>{person.avatar}</span>
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                color: theme.colors.text,
              }}>{person.name}</div>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '11px',
                color: theme.colors.textDim,
              }}>{person.reason}</div>
            </div>
            <div style={{
              fontFamily: theme.fonts.header,
              fontSize: '10px',
              padding: '4px 8px',
              backgroundColor: riskColors[person.risk],
              color: theme.colors.bg,
              textTransform: 'uppercase',
            }}>{person.risk}</div>
          </div>
        ))}
      </div>

      {/* Streak leaders */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.success,
          marginBottom: '8px',
        }}>🔥 STREAK LEADERS</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {data.streakData.map((person) => (
            <div
              key={person.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 10px',
                backgroundColor: theme.colors.bg,
                border: `2px solid ${theme.colors.border}`,
              }}
            >
              <span style={{ fontSize: '14px', marginRight: '8px' }}>{person.avatar}</span>
              <span style={{
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                color: theme.colors.text,
                flex: 1,
              }}>{person.name}</span>
              <span style={{
                fontFamily: theme.fonts.header,
                fontSize: '12px',
                color: person.status === 'active' ? theme.colors.success : theme.colors.warning,
              }}>{person.streak} days</span>
            </div>
          ))}
        </div>
      </div>

      {/* Activity patterns */}
      <div>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>ACTIVITY PATTERNS</h3>
        {data.activityPatterns.map((person) => (
          <div
            key={person.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '6px 10px',
              marginBottom: '4px',
            }}
          >
            <span style={{ fontSize: '14px', marginRight: '8px' }}>{person.avatar}</span>
            <span style={{
              fontFamily: theme.fonts.body,
              fontSize: '12px',
              color: theme.colors.textMuted,
            }}>{person.peakHours.join(', ')} • avg {person.avgSessionTime}m/session</span>
          </div>
        ))}
      </div>
    </PixelCard>
  );
}

// Agent Adoption Card - who uses what models, adoption over time
function AgentAdoptionCard({ data }) {
  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          🤖 AGENT ADOPTION
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Who uses which models + adoption trends
        </p>
      </div>

      {/* Adoption trend chart */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ height: '120px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.adoptionTrend}>
              <XAxis
                dataKey="week"
                style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
                stroke={theme.colors.textDim}
              />
              <YAxis
                style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
                stroke={theme.colors.textDim}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors.card,
                  border: `1px solid ${theme.colors.border}`,
                  fontFamily: theme.fonts.body,
                }}
              />
              <Area
                type="monotone"
                dataKey="sonnet"
                stackId="1"
                stroke={theme.colors.primary}
                fill={theme.colors.primary}
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="opus"
                stackId="1"
                stroke={theme.colors.secondary}
                fill={theme.colors.secondary}
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="haiku"
                stackId="1"
                stroke={theme.colors.success}
                fill={theme.colors.success}
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Person adoption table */}
      <div>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>ADOPTION BY PERSON</h3>
        <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
          {data.byPerson.map((person) => (
            <div
              key={person.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 10px',
                marginBottom: '4px',
                backgroundColor: theme.colors.bg,
                border: `2px solid ${theme.colors.border}`,
              }}
            >
              <span style={{ fontSize: '14px', marginRight: '8px' }}>{person.avatar}</span>
              <div style={{ flex: 1 }}>
                <span style={{
                  fontFamily: theme.fonts.body,
                  fontSize: '13px',
                  color: theme.colors.text,
                  marginRight: '8px',
                }}>{person.name}</span>
                <span style={{
                  fontFamily: theme.fonts.body,
                  fontSize: '11px',
                  color: person.primaryModel.includes('Opus') ? theme.colors.secondary :
                         person.primaryModel.includes('Sonnet') ? theme.colors.primary :
                         theme.colors.success,
                }}>{person.primaryModel}</span>
              </div>
              <div style={{
                fontFamily: theme.fonts.header,
                fontSize: '10px',
                color: person.adoption >= 90 ? theme.colors.success :
                       person.adoption >= 75 ? theme.colors.primary :
                       theme.colors.warning,
              }}>{person.adoption}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Model switching */}
      <div style={{
        marginTop: '12px',
        padding: '10px',
        backgroundColor: theme.colors.bg,
        border: `2px solid ${theme.colors.border}`,
        fontFamily: theme.fonts.body,
        fontSize: '11px',
      }}>
        <div style={{ color: theme.colors.textMuted, marginBottom: '6px' }}>MODEL SWITCHES (THIS WEEK)</div>
        {data.modelSwitching.map((sw, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '4px 0',
            color: theme.colors.text,
          }}>
            <span>{sw.from} → {sw.to}</span>
            <span style={{ color: theme.colors.textDim }}>{sw.reason} ({sw.count})</span>
          </div>
        ))}
      </div>
    </PixelCard>
  );
}

// People + Agent Synergy Card - collaboration patterns
function CollaborationSynergyCard({ data }) {
  return (
    <PixelCard className="p-6">
      <div style={{ marginBottom: '16px' }}>
        <h2 style={{
          fontFamily: theme.fonts.header,
          fontSize: '12px',
          color: theme.colors.textMuted,
        }}>
          🤝 PEOPLE + AGENT SYNERGY
        </h2>
        <p style={{
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
          marginTop: '4px',
        }}>
          Collaboration patterns between humans and AI
        </p>
      </div>

      {/* Top person-model pairs */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>TOP PAIRS</h3>
        {data.topPairs.map((pair) => (
          <div
            key={pair.person}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              marginBottom: '6px',
              backgroundColor: theme.colors.bg,
              borderTop: `2px solid ${theme.colors.border}`,
              borderRight: `2px solid ${theme.colors.border}`,
              borderBottom: `2px solid ${theme.colors.border}`,
              borderLeftWidth: `calc(2px + ${pair.synergy / 25}px)`,
              borderLeftColor: pair.synergy >= 90 ? theme.colors.success :
                               pair.synergy >= 80 ? theme.colors.primary :
                               theme.colors.warning,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                color: theme.colors.text,
              }}>{pair.person} + {pair.model}</div>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '11px',
                color: theme.colors.textDim,
              }}>{pair.sessions} sessions</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontFamily: theme.fonts.header,
                fontSize: '14px',
                color: pair.synergy >= 90 ? theme.colors.success :
                         pair.synergy >= 80 ? theme.colors.primary :
                         theme.colors.warning,
              }}>{pair.synergy}%</div>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '10px',
                color: theme.colors.textDim,
              }}>synergy</div>
            </div>
          </div>
        ))}
      </div>

      {/* Collaboration patterns */}
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>COLLABORATION PATTERNS</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Object.entries(data.collaborationPatterns).map(([type, info]) => (
            <div
              key={type}
              style={{
                padding: '10px',
                backgroundColor: theme.colors.bg,
                border: `2px solid ${type === 'deepWork' ? theme.colors.secondary :
                                   type === 'iterative' ? theme.colors.primary :
                                   theme.colors.success}`,
              }}
            >
              <div style={{
                fontFamily: theme.fonts.header,
                fontSize: '10px',
                color: theme.colors.textMuted,
                textTransform: 'uppercase',
                marginBottom: '4px',
              }}>
                {type === 'deepWork' ? '💎 DEEP WORK' :
                 type === 'iterative' ? '🔄 ITERATIVE' : '⚡ QUICK'}
              </div>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '12px',
                color: theme.colors.text,
              }}>
                {info.model} • avg {info.avgDuration}m
              </div>
              <div style={{
                fontFamily: theme.fonts.body,
                fontSize: '11px',
                color: theme.colors.textDim,
                marginTop: '2px',
              }}>
                {info.tasks.join(', ')}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tool usage by model */}
      <div>
        <h3 style={{
          fontFamily: theme.fonts.header,
          fontSize: '10px',
          color: theme.colors.textMuted,
          marginBottom: '8px',
        }}>TOOL USAGE BY MODEL</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          {Object.entries(data.toolUsageByModel).map(([model, tools]) => (
            <div
              key={model}
              style={{
                flex: 1,
                padding: '8px',
                backgroundColor: theme.colors.bg,
                border: `2px solid ${model.includes('Opus') ? theme.colors.secondary :
                                   model.includes('Sonnet') ? theme.colors.primary :
                                   theme.colors.success}`,
              }}
            >
              <div style={{
                fontFamily: theme.fonts.header,
                fontSize: '10px',
                color: theme.colors.textMuted,
                marginBottom: '4px',
              }}>{model.split(' ')[0]}</div>
              {Object.entries(tools).map(([tool, pct]) => (
                <div key={tool} style={{
                  fontFamily: theme.fonts.body,
                  fontSize: '10px',
                  color: theme.colors.textDim,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}>
                  <span>{tool}</span>
                  <span>{pct}%</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </PixelCard>
  );
}

// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================

export default function OtelMetricsPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive mode from URL path
  const pathMode = location.pathname.includes('/admin') ? 'admin' : 'personal';

  // Get timeRange from query params, default to 'week'
  const timeRange = searchParams.get('range') || 'week';

  const [userRole, setUserRole] = useState(pathMode); // 'personal' | 'admin'
  const [mounted, setMounted] = useState(false);

  // Update userRole when URL changes
  useEffect(() => {
    setUserRole(pathMode);
  }, [pathMode]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Get data filtered by time range
  const displayData = getDataByTimeRange(timeRange, mockData);

  // Handler to update range in query params
  const setRange = (newRange) => {
    setSearchParams({ range: newRange });
  };

  if (!mounted) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: theme.colors.bg,
        fontFamily: theme.fonts.header,
        fontSize: '14px',
        color: theme.colors.primary,
      }}>
        LOADING...
      </div>
    );
  }

  return (
    <div className="crt" style={{
      backgroundColor: theme.colors.bg,
      minHeight: '100vh',
      fontFamily: theme.fonts.body,
      color: theme.colors.text,
      position: 'relative',
    }}>
      <CRTOverlay />

      {/* Header */}
      <Header userRole={userRole} />

      {/* Live ticker */}
      <LiveTicker live={displayData.live} />

      {/* Main content */}
      <main style={{
        padding: '24px',
        maxWidth: '1600px',
        margin: '0 auto',
      }}>
        {/* Controls bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}>
          <TimeRangeToggle range={timeRange} setRange={setRange} />
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textMuted,
          }}>
            Last updated: <span style={{ color: theme.colors.success }}>JUST NOW</span>
          </div>
        </div>

        {/* Dashboard grid - Different layouts based on role */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: '16px',
        }}>

          {userRole === 'personal' ? (
            <>
              {/* PERSONAL VIEW - Individual performance metrics */}
              {/* Top row - Key metrics (3 columns, same height) */}
              <div style={{ gridColumn: 'span 4' }}>
                <QualityScoreCard data={displayData.personal} />
              </div>

              <div style={{ gridColumn: 'span 4' }}>
                <TokenEfficiencyCard data={displayData.personal} team={displayData.team} />
              </div>

              <div style={{ gridColumn: 'span 4' }}>
                <SessionActivityCard data={displayData.personal} />
              </div>

              {/* Second row */}
              <div style={{ gridColumn: 'span 8' }}>
                <PREfficiencyCard data={displayData.personal} />
              </div>

              <div style={{ gridColumn: 'span 4' }}>
                <ToolAcceptanceCard data={displayData.personal} />
              </div>

              {/* Third row (4 columns, same height) */}
              <div style={{ gridColumn: 'span 3' }}>
                <ModelComparisonCard team={displayData.team} />
              </div>

              <div style={{ gridColumn: 'span 3' }}>
                <ReversionRateCard data={displayData.personal} team={displayData.team} />
              </div>

              <div style={{ gridColumn: 'span 3' }}>
                <ProductivityHeatmap data={displayData.personal} />
              </div>

              <div style={{ gridColumn: 'span 3' }}>
                <SessionActivityCard data={displayData.personal} />
              </div>

              {/* Fourth row - Full width leaderboard (anonymized) */}
              <div style={{ gridColumn: 'span 12' }}>
                <TeamLeaderboard
                  team={displayData.team}
                  userRole={userRole}
                  adminData={displayData.adminData}
                />
              </div>
            </>
          ) : (
            <>
              {/* ADMIN VIEW - People + Agent focused */}
              {/* Top row - Team roster (full width, spans 7) + Cost allocation (spans 5) */}
              <div style={{ gridColumn: 'span 7' }}>
                <TeamRosterCard data={displayData.adminData.teamRoster} />
              </div>

              <div style={{ gridColumn: 'span 5' }}>
                <CostAllocationCard data={displayData.adminData.costs} />
              </div>

              {/* Second row - Team Health (spans 4) + Agent Adoption (spans 4) + Collaboration Synergy (spans 4) */}
              <div style={{ gridColumn: 'span 4' }}>
                <TeamHealthCard data={displayData.adminData.teamHealth} />
              </div>

              <div style={{ gridColumn: 'span 4' }}>
                <AgentAdoptionCard data={displayData.adminData.agentAdoption} />
              </div>

              <div style={{ gridColumn: 'span 4' }}>
                <CollaborationSynergyCard data={displayData.adminData.collaboration} />
              </div>

              {/* Third row - Team leaderboard (de-anonymized for admin) */}
              <div style={{ gridColumn: 'span 12' }}>
                <TeamLeaderboard
                  team={displayData.team}
                  userRole={userRole}
                  adminData={displayData.adminData}
                />
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <footer style={{
          marginTop: '32px',
          padding: '16px',
          borderTop: `2px solid ${theme.colors.border}`,
          textAlign: 'center',
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
        }}>
          <div>
            📊 Powered by OpenTelemetry • Data refreshes every 10s
          </div>
          <div style={{ marginTop: '4px' }}>
            {userRole === 'admin' ? (
              <>👑 Admin Mode • People + Agent Management • {displayData.adminData.teamRoster.length} team members</>
            ) : (
              <>👤 Personal View • Your Performance Metrics</>
            )}
          </div>
        </footer>
      </main>
    </div>
  );
}
